import { useState, type MouseEvent } from "react";
import { useProposalFieldHistoryRequest } from "work/proposals/negotiation/proposal-field-history-request-context";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * Shared open/close state for a field-history trigger button — `ProposalFieldHistoryButton`'s icon
 * button and `ProposalFieldTimelineDropdown`'s icon button both open a popover/menu the same way.
 *
 * The trigger commonly sits inside a composite field control — a MUI X DatePicker's input adornment,
 * a text field's end adornment — whose ancestor has its own click handler (e.g. swapping a date field
 * into an editing state). An unguarded click bubbles into that ancestor on the same click that opened
 * the popover/menu, and the ancestor's handler can re-render or unmount the trigger's row, taking the
 * just-opened popover down with it before the user ever sees it (observed on the draft page's
 * "Response Due Date" field, whose date-picker adornment swaps branches on click). Stopping
 * propagation — and the default action, since these buttons render inside form controls — at the
 * trigger keeps the click from ever reaching that ancestor.
 *
 * Pass `field` to let the page open this trigger without a click: the change traverser stops at a
 * field carrying an unread change and asks that field's history to open itself (see
 * proposal-field-history-request-context.ts). Such a request has no click event to take an anchor
 * from, so the trigger reports its own element through `triggerRef` — a callback ref into state
 * rather than a ref object, because an anchor read from a ref during render is neither allowed nor
 * stable.
 */
export function useProposalFieldHistoryPopoverTrigger<TElement extends HTMLElement = HTMLElement>(
  field?: ProposalField,
) {
  const [anchorEl, setAnchorEl] = useState<TElement | null>(null);
  const [triggerEl, setTriggerEl] = useState<TElement | null>(null);
  const { isRequested, onHonored } = useProposalFieldHistoryRequest(field);

  function open(event: MouseEvent<TElement>) {
    // MUI input adornments can keep the field itself focused when an adornment button is clicked.
    // Move focus to the trigger explicitly so a debounced field flushes its local value during this
    // opening click, before the user can choose a history entry on a later click. Otherwise the
    // still-pending field change can run alongside (or after) the revert and make the first revert
    // appear to do nothing; reopening the menu then works only because that pending edit has cleared.
    event.currentTarget.focus();
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }

  function close() {
    setAnchorEl(null);
    // Closing a page-requested history answers the request; leaving it standing would reopen the
    // popover the reader just dismissed.
    if (isRequested) onHonored?.();
  }

  return {
    anchorEl: anchorEl ?? triggerEl,
    // A request only opens the popover once the trigger has reported an element to anchor it to.
    isOpen: anchorEl !== null || (isRequested && triggerEl !== null),
    open,
    close,
    triggerRef: setTriggerEl,
  };
}
